import { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Capacitor } from "@capacitor/core";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { Globe, ShieldAlert, X } from "lucide-react";

import { useScreen0Language } from "@/features/language";
import { getTranslations } from "@/i18n";
import { installWebFilter, orchestrateNativeWebFilter } from "@/lib/native/webFilter";
import type { Rule, Target } from "@/lib/rules/ruleTypes";

type VpnInstallPromptModalProps = {
  open: boolean;
  rules: Rule[];
  targets: Target[];
  scheduleNow: Date;
  onClose: () => void;
  onInstalled: () => void;
};

const POPOVER_SPRING = { type: "spring" as const, stiffness: 420, damping: 40, mass: 0.9 };

export function VpnInstallPromptModal({
  open,
  rules,
  targets,
  scheduleNow,
  onClose,
  onInstalled,
}: VpnInstallPromptModalProps) {
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode);
  const copy = t.rules.vpnInstallPrompt;
  const vpnBody =
    Capacitor.getPlatform() === "android" ? copy.bodyAndroid : copy.body;
  const prefersReducedMotion = useReducedMotion() ?? false;
  const titleId = useId();
  const closeRef = useRef<HTMLButtonElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = useCallback(() => {
    if (busy) return;
    onClose();
  }, [busy, onClose]);

  useEffect(() => {
    if (!open) {
      setError(null);
      setBusy(false);
      return;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        close();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, close]);

  useEffect(() => {
    if (open) closeRef.current?.focus();
  }, [open]);

  async function handleInstall() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const installed = await installWebFilter();
      if (!installed.installed) {
        setError(installed.reason ?? installed.status);
        return;
      }
      await orchestrateNativeWebFilter(rules, targets, scheduleNow);
      onInstalled();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  }

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {open ? (
        <div className="fixed inset-0 z-[120] flex items-end justify-center p-4 sm:items-center">
          <motion.button
            type="button"
            className="absolute inset-0 bg-[oklch(8%_0.02_165/0.72)] backdrop-blur-sm"
            aria-label={copy.dismissAria}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: prefersReducedMotion ? 0 : 0.2 }}
            onClick={close}
          />
          <motion.section
            id="vpn-install-prompt-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            className="liquid-glass-panel relative z-10 grid w-full max-w-md gap-4 p-5 text-left"
            initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 16, scale: 0.98 }}
            animate={prefersReducedMotion ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
            exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 10, scale: 0.99 }}
            transition={prefersReducedMotion ? { duration: 0 } : POPOVER_SPRING}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 flex size-10 shrink-0 items-center justify-center rounded-full bg-[oklch(24%_0.04_155)] text-[oklch(78%_0.08_155)]">
                  <Globe size={20} aria-hidden />
                </span>
                <div className="grid gap-1">
                  <h2 id={titleId} className="text-lg font-semibold text-[oklch(92%_0.02_150)]">
                    {copy.title}
                  </h2>
                  <p className="text-sm leading-6 text-[oklch(80%_0.025_155)]">{vpnBody}</p>
                </div>
              </div>
              <button
                ref={closeRef}
                type="button"
                className="icon-button shrink-0"
                aria-label={copy.dismissAria}
                onClick={close}
                disabled={busy}
              >
                <X size={18} aria-hidden />
              </button>
            </div>

            {error ? (
              <div className="liquid-glass-alert-warning flex gap-2 p-3 text-sm text-[oklch(82%_0.055_85)]">
                <ShieldAlert size={16} className="mt-0.5 shrink-0" aria-hidden />
                <p className="break-words font-mono text-xs leading-5">{error}</p>
              </div>
            ) : null}

            <div className="grid gap-2 sm:grid-cols-2">
              <button
                type="button"
                className="btn-secondary w-full"
                onClick={close}
                disabled={busy}
              >
                {copy.notNow}
              </button>
              <button
                type="button"
                className="btn-primary w-full"
                onClick={() => void handleInstall()}
                disabled={busy}
              >
                {busy ? copy.installing : copy.install}
              </button>
            </div>
          </motion.section>
        </div>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}
